// ---------- la sauvegarde du joueur, et la fusion de deux sauvegardes ----------
// Lue par le jeu (src/store.ts, src/sync.ts) ET par le Worker (backend/src/routes/account.ts) :
// la règle de fusion est écrite UNE fois. Si le serveur et l'appareil fusionnaient chacun à leur
// façon, une sauvegarde ferait des allers-retours sans jamais se stabiliser — chacun « corrigeant »
// ce que l'autre vient d'écrire.
//
// POURQUOI PAS L'UNION DE CODIGRID. Là-bas, une sauvegarde est la liste des cases peintes, et
// peindre deux fois une case ne fait rien : la liste ne fait que croître, et deux appareils se
// fusionnent par une union d'ensembles. Ici, une partie en cours DÉCROÎT : on efface une lettre
// fausse (play.ts, `erase`), on recommence une partie finie (« Rejouer », store.ts `restart`). Une
// union ferait revenir chaque lettre effacée. D'où une règle en trois morceaux, un par nature de
// donnée :
//
//   - ce qui est GAGNÉ ne se perd jamais : les étoiles sont un max, le mot mystère trouvé reste ;
//   - le PLATEAU d'une partie (lettres, indices, révélations) est pris ENTIER à la sauvegarde la
//     plus récente — jamais mélangé case par case, ce qui inventerait une grille que personne n'a
//     jouée. « Plus récente » se lit d'abord à la GÉNÉRATION (`n`, montée par chaque « Rejouer »),
//     puis à l'horodatage (`t`) ;
//   - ce qui ne fait que s'ajouter se fusionne par union : les parties passées d'un chapitre.
//
// LA GÉNÉRATION, et pas seulement l'horodatage, pour « Rejouer ». C'est ce qui remplace le DELETE de
// Codigrid : recommencer n'efface rien sur le serveur, il pousse un plateau vide d'une génération
// de plus, qui l'emporte sur l'ancien plateau plein quelle que soit l'heure des deux téléphones.
// Avec le seul horodatage, un appareil dont l'horloge retarde ferait revenir la grille remplie
// qu'on vient de jeter — le bogue que Codigrid décrit sans le régler (« a generation counter is
// what fixes it »). Entre deux sauvegardes de MÊME génération, l'horloge tranche : c'est le seul
// arbitre disponible, et l'enjeu est quelques lettres d'une partie en cours, pas des étoiles.
//
// ENVOYER LA PARTIE ENTIÈRE, jamais un delta, comme Codigrid : la règle est idempotente
// (fusionner deux fois ne change rien) et une écriture perdue en route est réparée par la suivante.

/** Une partie : la forme stockée par l'appareil (`verbigrid:progress:v2`) ET en base. */
export interface PuzzleSave {
  /** case "ligne,lettre" → lettre posée */
  e: Record<string, string>;
  /** ligne → nombre d'indices pris (0 à 2) */
  h: Record<number, number>;
  /** lignes révélées d'un coup */
  r: Record<number, 1>;
  /** cases remplies par une révélation (affichées en bleu, comme dans la maquette) */
  g: Record<string, 1>;
  /** finie, et ses étoiles — les meilleures obtenues */
  done?: number;
  /** son mot mystère, gardé à la fin : la collection du chapitre l'affiche sans recharger la
   *  partie (le résumé du chemin ne le donne pas, ce serait la réponse) */
  w?: string;
  /** la génération du plateau : 0 au départ, +1 à chaque « Rejouer ». Absente = 0. */
  n?: number;
  /** quand le plateau a changé pour la dernière fois, en millisecondes epoch, à l'horloge de
   *  l'appareil. Absent = 0 : un plateau jamais horodaté perd contre n'importe quel autre. */
  t?: number;
}

export interface DailyStreak {
  /** dernier jour (UTC) où le mot du jour a été trouvé, et la série qui y mène */
  last: string;
  count: number;
}

/** Ce qui voyage entre l'appareil et le Worker. Un envoi peut n'en porter qu'une partie : seules
 *  les parties et les chapitres qui ont changé partent (cf. src/sync.ts). */
export interface SaveState {
  puzzles: Record<string, PuzzleSave>;
  /** chapitre → parties passées, dans l'ordre où elles l'ont été */
  skipped: Record<string, string[]>;
  streak?: DailyStreak;
}

/** Le plateau seul : ce qui se remplace en bloc. */
const board = (p: PuzzleSave): Pick<PuzzleSave, 'e' | 'h' | 'r' | 'g' | 'n' | 't'> => ({
  e: p.e,
  h: p.h,
  r: p.r,
  g: p.g,
  ...(p.n ? { n: p.n } : {}),
  ...(p.t ? { t: p.t } : {}),
});

/** `b` est-il plus récent que `a` ? Génération d'abord, horloge ensuite. À égalité, NON : la
 *  fusion garde alors `a`, ce qui la rend déterministe sans la rendre arbitraire — deux plateaux
 *  de même génération et de même milliseconde sont, en pratique, le même plateau. */
const newer = (a: PuzzleSave, b: PuzzleSave): boolean =>
  (b.n ?? 0) !== (a.n ?? 0) ? (b.n ?? 0) > (a.n ?? 0) : (b.t ?? 0) > (a.t ?? 0);

/** Deux sauvegardes d'une même partie. `a` absente : `b` telle quelle, et inversement. */
export function mergePuzzle(a: PuzzleSave | undefined, b: PuzzleSave | undefined): PuzzleSave {
  if (!a) return b ?? { e: {}, h: {}, r: {}, g: {} };
  if (!b) return a;
  const out: PuzzleSave = board(newer(a, b) ? b : a);
  const done = Math.max(a.done ?? 0, b.done ?? 0);
  if (done > 0) out.done = done;
  // Le mot mystère est le même des deux côtés (une partie publiée ne change pas de mot, cf.
  // CLAUDE.md) ; on prend celui qui existe.
  const w = a.w ?? b.w;
  if (w !== undefined) out.w = w;
  return out;
}

/** Les parties passées : l'union, dans l'ordre de `a` puis ce que `b` ajoute. L'ordre compte un
 *  peu (la première passée qui attend est celle que la fin de partie propose, src/quest.ts
 *  `nextAfter`) ; il n'a pas à être juste au téléphone près. Une passe ne se reprend jamais
 *  (elle se RENDS en finissant la partie, ce que `isDone` lit), donc l'union ne fait rien revenir. */
export function mergeSkipped(a: readonly string[] = [], b: readonly string[] = []): string[] {
  const out = [...a];
  for (const id of b) if (!out.includes(id)) out.push(id);
  return out;
}

/** La série du jour : la plus récente. Même jour des deux côtés, la plus longue — deux appareils
 *  qui ont trouvé le même mot du jour ont la même série, sauf si l'un l'a commencée plus tôt. Une
 *  série ne se fusionne pas par addition : deux téléphones ne font pas deux jours. */
export function mergeStreak(a?: DailyStreak, b?: DailyStreak): DailyStreak | undefined {
  if (!a || !b) return a ?? b;
  if (a.last !== b.last) return a.last > b.last ? a : b;
  return a.count >= b.count ? a : b;
}

/** Deux sauvegardes entières (ou partielles : ce qui manque d'un côté est pris de l'autre). */
export function mergeSaves(a: SaveState, b: SaveState): SaveState {
  const puzzles: Record<string, PuzzleSave> = { ...a.puzzles };
  for (const [id, p] of Object.entries(b.puzzles)) puzzles[id] = mergePuzzle(a.puzzles[id], p);
  const skipped: Record<string, string[]> = { ...a.skipped };
  for (const [id, l] of Object.entries(b.skipped)) skipped[id] = mergeSkipped(a.skipped[id], l);
  const streak = mergeStreak(a.streak, b.streak);
  return { puzzles, skipped, ...(streak ? { streak } : {}) };
}

/** Deux parties identiques ? Pour ne réécrire en base — et ne renvoyer sur le réseau — que ce qui
 *  a vraiment bougé. Une comparaison de JSON suffit : les deux côtés construisent leurs objets
 *  avec les mêmes fonctions, donc dans le même ordre de clés... sauf `e`, rempli dans l'ordre où le
 *  joueur a tapé. D'où le tri des clés. */
export function samePuzzle(a: PuzzleSave | undefined, b: PuzzleSave | undefined): boolean {
  return canon(a) === canon(b);
}

/** La forme canonique d'une valeur (clés triées), pour comparer deux sauvegardes par leur texte. */
export function canon(v: unknown): string {
  if (v === undefined) return 'undefined';
  return JSON.stringify(v, (_, x: unknown) =>
    x && typeof x === 'object' && !Array.isArray(x)
      ? Object.fromEntries(Object.entries(x).sort(([p], [q]) => (p < q ? -1 : p > q ? 1 : 0)))
      : x,
  );
}

/** Une partie vide, sans rien de gagné : elle n'a rien à apprendre à personne, et ne part pas. */
export const isBlank = (p: PuzzleSave): boolean =>
  p.done === undefined &&
  !Object.keys(p.e).length &&
  !Object.keys(p.h).length &&
  !Object.keys(p.r).length &&
  !Object.keys(p.g).length &&
  !p.n;
